import { Dialog, useDialogManager } from '../../../../../widgets/dialog';
import { FormControl } from '../../../../../widgets/formControl';
import { Input } from '../../../../../widgets/input';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../../widgets/notificationToaster';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import { commitMutation } from 'react-relay'; // Commentato - usiamo 3D core
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
// import environment from 'src/relay/environment'; // Commentato - usiamo 3D core
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import PaletteSolidIcon from 'src/shared/icons/palette-solid.svg';
import styled from 'styled-components';
import NewIcon from '../icons/addIcon.svg';
import CloneIcon from '../icons/cloneIcon.svg';
import FolderIcon from '../icons/folderCloseIcon.svg';
import GroupIcon from '../icons/groupIcon.svg';
import ImportIcon from '../icons/importIcon.svg';
import RemoveIcon from '../icons/removeIcon.svg';
import RenameIcon from '../icons/renameIcon.svg';
import SaveIcon from '../icons/saveIcon.svg';
import MaterialImportDialog from './MaterialImportDialog';
import MaterialOverlaySimple from './MaterialOverlaySimple';
// import { createLibraryMaterialMutation } from './query/createLibraryMaterialMutation'; // Commentato - usiamo 3D core

import { ToolGrid } from '../toolsgrid';
import TreeComponent, { TreeHandle } from '../TreeComponent';
import { BaseTreeNode, useTreeNodeManager } from './hooks/useTreeNodeManager';

export interface MaterialNode extends BaseTreeNode {
    // Proprietà base di BaseTreeNode (id, name, children) sono ereditate
    type: 'material' | 'group'; // Indica il tipo di nodo
    customIcon?: React.ReactNode; // Icona personalizzata per il nodo
    notTurnableIntoFolder?: boolean; // Impedisce la trasformazione in cartella
    visible?: boolean; // Indica se il nodo è visibile
    data: {
        // L'oggetto materiale BABYLON completo per accesso diretto
        material?: any;
        // Proprietà scomposte per compatibilità
        variantId?: number;
        materialType?: string;
        diffuseColor?: string;
        specularColor?: string;
        emissiveColor?: string;
        roughness?: number;
        metallic?: number;
        // Proprietà dal sceneEditor
        _internalId?: string;
        enabled?: boolean;
        visible?: boolean;
        // Per i gruppi
        groupName?: string;
        materialIds?: string[]; // Array di ID dei materiali nel gruppo
    };
}

const UpgradeBanner = styled(UpgradeDialog)`
    width: 400px;
    height: 300px;
`;

interface MaterialsEditorProps {
    materials?: any[];
    selectedMaterials?: any[];
    sceneEditor?: any;
    zkScene?: any;
    onMaterialsUpdated?: (materials: any[]) => void;
    treeRef: React.RefObject<TreeHandle>;
    additionalFeatures?: any;
}

const MaterialsEditor: FC<MaterialsEditorProps> = ({
    treeRef,
    materials = [],
    selectedMaterials = [],
    sceneEditor,
    zkScene,
    onMaterialsUpdated,
    additionalFeatures
}) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { subscription, sellerMarketplace, updateSubscription } = useSubscriptionData();

    // Ricevi materiali dal SceneEditor via props

    // State per il refresh trigger (come objectEditor)
    const [materialRefreshTrigger, setMaterialRefreshTrigger] = useState(false);

    // State locale per i materiali (come objectEditor con meshes)
    const [localMaterials, setLocalMaterials] = useState<any[]>(() => {
        const initialMaterials = sceneEditor?.getAllMaterials() || materials || [];
        console.log('🚀 MaterialsEditor: Initial materials loaded:', {
            count: initialMaterials.length,
            sceneEditorExists: !!sceneEditor,
            fromSceneEditor: !!sceneEditor?.getAllMaterials()?.length,
            fromProps: materials.length
        });
        return initialMaterials;
    });

    // State per gestire il displayOrder dei gruppi (come nel vecchio SceneEditorPage)
    const [groupMaterialsDisplayOrder, setGroupMaterialsDisplayOrder] = useState<Map<string, number>>(new Map());

    // Set di materiali che sono stati manualmente disassociati dai gruppi
    const [manuallyUngroupedMaterials, setManuallyUngroupedMaterials] = useState<Set<string>>(new Set());

    // Ref per il flag di importazione (per accesso immediato, non asincrono come useState)

    // Ref per tenere i nodi correnti durante l'import
    const currentNodesRef = useRef<MaterialNode[]>([]);

    // Ref per il flag di drag in corso (per evitare refresh durante il drag)
    const isDraggingRef = useRef(false);

    // Converti i materiali dal SceneEditor in MaterialNode per il tree
    const convertToMaterialNodes = useCallback(
        (sceneMaterials: any[]): MaterialNode[] => {
            // Usa getGroupedMaterials() se disponibile per avere la struttura corretta con i gruppi
            if (sceneEditor && sceneEditor.getGroupedMaterials) {
                try {
                    const groupedMaterials = sceneEditor.getGroupedMaterials();

                    const result: MaterialNode[] = [];
                    const materialsToForceInRoot: any[] = []; // Materiali da forzare nella radice

                    groupedMaterials.forEach((group: any) => {
                        // Il gruppo "dummy" contiene materiali non raggruppati - non mostrare il gruppo stesso
                        if (group.groupName === '_dummy_' || group.groupName === '' || !group.groupName) {
                            // Materiali del gruppo dummy - aggiungili direttamente alla radice (senza gruppo visibile)
                            if (group.materials) {
                                group.materials.forEach((material: any, index: number) => {
                                    // Assicurati che il materiale abbia metadata
                                    if (!material.metadata) {
                                        material.metadata = {};
                                    }
                                    // Se non ha displayOrder, assegnalo basato sull'index
                                    if (material.metadata.displayOrder === undefined) {
                                        material.metadata.displayOrder = index;
                                    }

                                    result.push({
                                        id: material._internalId || material.id || `material-${index}`,
                                        name: material.name || `Material ${index + 1}`,
                                        type: 'material' as const,
                                        children: [],
                                        customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />,
                                        notTurnableIntoFolder: true,
                                        data: {
                                            material: material,
                                            variantId: material.variantId || 1,
                                            materialType: material.materialType || 'pbr',
                                            diffuseColor: material.diffuseColor || '#FFFFFF',
                                            specularColor: material.specularColor || '#FFFFFF',
                                            emissiveColor: material.emissiveColor || '#000000',
                                            roughness: material.roughness ?? 0.5,
                                            metallic: material.metallic ?? 0.0,
                                            _internalId: material._internalId,
                                            enabled: material.enabled ?? true,
                                            visible: material.visible ?? true
                                        }
                                    });
                                });
                            }
                        } else {
                            // Gruppi reali (non dummy) - crea un nodo gruppo visibile

                            // FILTRA i materiali manualmente disassociati da questo gruppo
                            const groupMaterials =
                                group.materials?.filter((material: any) => {
                                    // Usa la stessa logica di generazione ID dei nodi TreeComponent
                                    const materialId = material._internalId || material.id;
                                    const isManuallyUngrouped = manuallyUngroupedMaterials.has(materialId);
                                    if (isManuallyUngrouped) {
                                        materialsToForceInRoot.push(material);
                                    }
                                    return !isManuallyUngrouped;
                                }) || [];

                            // Se il gruppo non ha più materiali, saltalo
                            if (groupMaterials.length === 0) {
                                return;
                            }

                            const groupNode: MaterialNode = {
                                id: `group-${group.guid || Date.now()}`,
                                name: group.groupName,
                                type: 'group' as const,
                                children: [],
                                customIcon: <FolderIcon style={{ width: '16px', height: '16px', fill: '#666' }} />,
                                visible: true, // I gruppi sono sempre visibili
                                data: {
                                    groupName: group.groupName,
                                    materialIds: groupMaterials.map((m: any) => m.id)
                                }
                            };

                            // Aggiungi i materiali come figli del gruppo
                            if (groupMaterials.length > 0) {
                                groupNode.children = groupMaterials.map((material: any, index: number) => {
                                    // Assicurati che il materiale abbia metadata
                                    if (!material.metadata) {
                                        material.metadata = {};
                                    }
                                    // Per i materiali nei gruppi, il displayOrder è l'index del materiale nel gruppo
                                    if (material.metadata.displayOrder === undefined) {
                                        material.metadata.displayOrder = index;
                                    }

                                    return {
                                        id: material._internalId || material.id || `material-${index}`,
                                        name: material.name || `Material ${index + 1}`,
                                        type: 'material' as const,
                                        children: [],
                                        customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />,
                                        notTurnableIntoFolder: true,
                                        data: {
                                            material: material,
                                            variantId: material.variantId || 1,
                                            materialType: material.materialType || 'pbr',
                                            diffuseColor: material.diffuseColor || '#FFFFFF',
                                            specularColor: material.specularColor || '#FFFFFF',
                                            emissiveColor: material.emissiveColor || '#000000',
                                            roughness: material.roughness ?? 0.5,
                                            metallic: material.metallic ?? 0.0,
                                            _internalId: material._internalId,
                                            enabled: material.enabled ?? true,
                                            visible: material.visible ?? true
                                        }
                                    };
                                });
                            }

                            result.push(groupNode);
                        }
                    });

                    // Aggiungi i materiali manualmente disassociati dai gruppi alla radice
                    materialsToForceInRoot.forEach((material: any, index: number) => {
                        // Assicurati che il materiale abbia metadata
                        if (!material.metadata) {
                            material.metadata = {};
                        }
                        // Assegna un displayOrder elevato per metterli alla fine della lista
                        if (material.metadata.displayOrder === undefined) {
                            material.metadata.displayOrder = 1000 + index; // Elevato per metterli in fondo
                        }

                        const materialNode: MaterialNode = {
                            id: material._internalId || material.id || `material-ungrouped-${index}`,
                            name: material.name || `Material ${index + 1}`,
                            type: 'material' as const,
                            children: [],
                            customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />,
                            notTurnableIntoFolder: true,
                            data: {
                                material: material,
                                variantId: material.variantId || 1,
                                materialType: material.materialType || 'pbr',
                                diffuseColor: material.diffuseColor || '#FFFFFF',
                                specularColor: material.specularColor || '#FFFFFF',
                                emissiveColor: material.emissiveColor || '#000000',
                                roughness: material.roughness ?? 0.5,
                                metallic: material.metallic ?? 0.0,
                                _internalId: material._internalId,
                                enabled: material.enabled ?? true,
                                visible: material.visible ?? true
                            }
                        };

                        result.push(materialNode);
                    });

                    return result;
                } catch (error) {
                    console.warn('Error getting grouped materials, falling back to regular materials:', error);
                }
            }

            // Fallback alla vecchia logica se getGroupedMaterials non è disponibile
            const materialsToUse = sceneMaterials.length > 0 ? sceneMaterials : localMaterials;
            if (!materialsToUse || materialsToUse.length === 0) {
                return [];
            }

            return materialsToUse.map((material: any, index: number) => {
                // Assicurati che il materiale abbia metadata
                if (!material.metadata) {
                    material.metadata = {};
                }
                // Se non ha displayOrder, assegnalo basato sull'index
                if (material.metadata.displayOrder === undefined) {
                    material.metadata.displayOrder = index;
                }

                return {
                    id: material._internalId || material.id || `material-${index}`,
                    name: material.name || `Material ${index + 1}`,
                    type: 'material' as const, // Specifica che è un materiale
                    children: [],
                    customIcon: <PaletteSolidIcon style={{ width: '16px', height: '16px', fill: '#666' }} />, // Icona custom per i materiali
                    notTurnableIntoFolder: true, // Impedisce che i materiali diventino cartelle nel drag&drop
                    data: {
                        // Salva l'oggetto materiale completo per accesso diretto
                        material: material,
                        // Mantieni anche le proprietà scomposte per compatibilità
                        variantId: material.variantId || 1,
                        materialType: material.materialType || 'pbr',
                        diffuseColor: material.diffuseColor || '#FFFFFF',
                        specularColor: material.specularColor || '#FFFFFF',
                        emissiveColor: material.emissiveColor || '#000000',
                        roughness: material.roughness ?? 0.5,
                        metallic: material.metallic ?? 0.0,
                        _internalId: material._internalId,
                        enabled: material.enabled ?? true,
                        visible: material.visible ?? true
                    }
                };
            });
        },
        [localMaterials, sceneEditor]
    );

    // Converti i MaterialNode di nuovo in formato SceneEditor
    const convertToSceneMaterials = useCallback((materialNodes: MaterialNode[]) => {
        return materialNodes.map((node) => {
            // Se abbiamo il materiale originale, lo utilizziamo come base
            if (node.data.material) {
                return {
                    ...node.data.material,
                    // Sovrascrivi con eventuali modifiche dal nodo
                    id: node.id,
                    name: node.name,
                    enabled: node.data.enabled,
                    visible: node.data.visible
                };
            }

            // Altrimenti, crea il materiale dalle proprietà scomposte
            return {
                _internalId: node.data._internalId,
                id: node.id,
                name: node.name,
                variantId: node.data.variantId,
                materialType: node.data.materialType,
                diffuseColor: node.data.diffuseColor,
                specularColor: node.data.specularColor,
                emissiveColor: node.data.emissiveColor,
                roughness: node.data.roughness,
                metallic: node.data.metallic,
                enabled: node.data.enabled,
                visible: node.data.visible
            };
        });
    }, []);

    // Ottieni i materiali iniziali convertiti - USA QUELLI DALLO SCENE EDITOR
    // Rimosso initialMaterialsData - ora usiamo direttamente convertToMaterialNodes(sceneEditorMaterials)

    // Hook per la gestione del tree con dati reali - MATERIALI MEMOIZZATI PER EVITARE LOOP
    const materialNodes = useMemo(() => {
        // Se stiamo importando, non ricalcolare - mantieni i nodi attuali

        console.log('🧮 Recalculating materialNodes with localMaterials:', localMaterials.length);
        const nodes = convertToMaterialNodes(localMaterials);
        console.log('📊 Converted nodes:', nodes.length);

        // Implementa il sort come nel vecchio SceneEditorPage
        // Combina materiali e gruppi per un ordinamento globale
        const allItems: any[] = [];

        nodes.forEach((node) => {
            if (node.type === 'group') {
                // Per i gruppi, usa il displayOrder dalla Map
                const groupDisplayOrder = groupMaterialsDisplayOrder.get(node.data.groupName || '') ?? 0;
                allItems.push({ node, sortOrder: groupDisplayOrder, isGroup: true });
            } else if (node.type === 'material' && node.data.material) {
                // Per i materiali, usa metadata.displayOrder
                const materialDisplayOrder = node.data.material.metadata?.displayOrder ?? 0;
                allItems.push({ node, sortOrder: materialDisplayOrder, isGroup: false });
            }
        });

        // Ordina tutti gli elementi per sortOrder
        allItems.sort((a, b) => a.sortOrder - b.sortOrder);

        const sortedNodes = allItems.map((item) => item.node);
        console.log('📋 Final sorted nodes:', sortedNodes.length);

        return sortedNodes;
    }, [localMaterials, convertToMaterialNodes, groupMaterialsDisplayOrder, manuallyUngroupedMaterials]);

    // Ref per tenere riferimento alla funzione updateNode
    const updateNodeRef = useRef<((id: string, updates: Partial<MaterialNode>) => void) | null>(null);
    const treeStateRef = useRef<any>(null);

    // Funzione per creare il componente overlay
    const createMaterialOverlay = useCallback(
        (node: MaterialNode) => {
            return () => {
                // I nodi gruppo non hanno overlay
                if (node.type === 'group') {
                    return null;
                }

                // Usa il materiale BABYLON reale direttamente
                const material = node.data.material;

                if (!material) {
                    return <div>Material not found</div>;
                }

                // Callback per aggiornare immediatamente il nome del nodo nel tree
                const handleNameChanged = (newName: string) => {
                    if (updateNodeRef.current) {
                        updateNodeRef.current(node.id, { name: newName });
                    }
                };

                // Callback per chiudere l'overlay
                const handleClose = () => {
                    if (treeStateRef.current && treeStateRef.current.closeOverlay) {
                        treeStateRef.current.closeOverlay();
                    }
                };

                return (
                    <MaterialOverlaySimple
                        material={material}
                        onNameChanged={handleNameChanged}
                        additionalFeatures={additionalFeatures}
                        onClose={handleClose}
                    />
                );
            };
        },
        [additionalFeatures]
    );

    const {
        nodes,
        selectedIds,
        treeState,
        filter,
        setFilter,
        findNodeById,
        updateNode,
        cloneSelectedNodes,
        removeNodes,
        groupSelectedNodes,
        setNodes,
        filteredData
    } = useTreeNodeManager<MaterialNode>(materialNodes, createMaterialOverlay);

    // Aggiorna la ref con il treeState
    React.useEffect(() => {
        treeStateRef.current = treeState;
    }, [treeState]);

    // Funzione helper per trovare nodi che funziona sia con filteredData che con treeState originale
    const findNodeByIdUnified = useCallback(
        (nodeId: string): MaterialNode | null => {
            // Se c'è un filtro attivo, cerca prima nei filteredData
            if (filter && filteredData.length > 0) {
                const searchInFiltered = (nodes: MaterialNode[]): MaterialNode | null => {
                    for (const node of nodes) {
                        if (node.id === nodeId) return node;
                        if (node.children && node.children.length > 0) {
                            const found = searchInFiltered(node.children as MaterialNode[]);
                            if (found) return found;
                        }
                    }
                    return null;
                };

                const foundInFiltered = searchInFiltered(filteredData);
                if (foundInFiltered) return foundInFiltered;
            }

            // Se non trovato nei filteredData o non c'è filtro, cerca nel treeState originale
            const originalFound = findNodeById(nodeId);
            return originalFound || null;
        },
        [filter, filteredData, findNodeById]
    );

    // OVERRIDE del canTurnIntoFolder per permettere il drop materiale-su-materiale per il riordino
    const customCanTurnIntoFolder = useCallback(
        (node: MaterialNode) => {
            // I gruppi possono sempre essere target (per raggruppamento)
            if (node.type === 'group') {
                return true;
            }

            // I materiali possono essere target SOLO se:
            // 1. C'è un drag in corso
            // 2. Il nodo trascinato è un materiale (per il riordino)
            if (node.type === 'material' && treeState.draggedId) {
                const draggedNode = findNodeByIdUnified(treeState.draggedId);
                if (draggedNode?.type === 'material') {
                    return true; // Permetti il drop per il riordino
                }
            }

            // In tutti gli altri casi, usa la logica standard
            return !node.notTurnableIntoFolder;
        },
        [treeState.draggedId, findNodeByIdUnified]
    );

    // Aggiungi il componente overlay direttamente ai nodi del treeState (come objectEditor)
    useEffect(() => {
        treeState.nodes.forEach((node) => {
            if (!node.component) {
                node.component = createMaterialOverlay(node);
            }
        });
    }, [treeState.nodes, createMaterialOverlay]);

    // Crea un treeState per il TreeComponent che usa filteredData quando c'è un filtro attivo
    const treeStateForComponent = useMemo(() => {
        const sourceNodes = filter ? filteredData : treeState.nodes;
        console.log('🔄 MaterialsEditor: Creating treeStateForComponent from', filter ? 'filteredData' : 'treeState.nodes', ':', sourceNodes.length);

        const stateForComponent = {
            ...treeState,
            nodes: sourceNodes // Sostituisci solo i nodes con quelli filtrati se necessario
        };

        // Applica anche il customCanTurnIntoFolder al treeStateForComponent
        stateForComponent.canTurnIntoFolder = customCanTurnIntoFolder;

        return stateForComponent;
    }, [treeState, filteredData, filter, customCanTurnIntoFolder]);

    // Sostituisci il canTurnIntoFolder del treeState con il nostro custom
    useEffect(() => {
        if (treeState.canTurnIntoFolder !== customCanTurnIntoFolder) {
            treeState.canTurnIntoFolder = customCanTurnIntoFolder;
        }
    }, [treeState, customCanTurnIntoFolder]);

    // Aggiorna il ref con la funzione updateNode una volta disponibile
    // tiene l'aggiornamento del name attivo sia lato overlay che tree
    useEffect(() => {
        updateNodeRef.current = updateNode;
    }, [updateNode]);

    // useEffect per gestire il materialRefreshTrigger (come objectEditor con meshRefreshTrigger)
    useEffect(() => {
        if (materialRefreshTrigger && sceneEditor) {
            console.log('🔄 materialRefreshTrigger activated - getting fresh materials');
            const newMaterials = sceneEditor.getAllMaterials();
            console.log('📊 Fresh materials count:', newMaterials.length);
            setLocalMaterials(newMaterials);
            setMaterialRefreshTrigger(false);
        }
    }, [materialRefreshTrigger, sceneEditor]);

    // Callback per aggiornare i materiali nel SceneEditor quando il tree cambia
    const handleTreeDataChanged = useCallback(
        (updatedNodes: MaterialNode[]) => {
            if (onMaterialsUpdated) {
                const sceneMaterials = convertToSceneMaterials(updatedNodes);
                onMaterialsUpdated(sceneMaterials);
            }
            if (sceneEditor && sceneEditor.updateMaterials) {
                sceneEditor.updateMaterials(updatedNodes);
            }
        },
        [convertToSceneMaterials, onMaterialsUpdated, sceneEditor]
    );

    // Struttura dati standard per nuovi materiali usando SceneEditor
    const createNewMaterial = useCallback(
        (name: string): MaterialNode => {
            const materialId = `material-${Date.now()}`;

            // Usa SceneEditor per creare un nuovo materiale PBR
            let newMaterial: BABYLON.Material | null = null;
            if (sceneEditor && sceneEditor.createMaterial) {
                newMaterial = sceneEditor.createMaterial(name, 'PBR');
            }

            // Se la creazione è fallita, crea un materiale mock come fallback
            if (!newMaterial) {
                newMaterial = {
                    id: materialId,
                    name,
                    materialType: 'pbr',
                    diffuseColor: '#FFFFFF',
                    specularColor: '#FFFFFF',
                    emissiveColor: '#000000',
                    roughness: 0.5,
                    metallic: 0.0,
                    enabled: true,
                    visible: true,
                    metadata: { displayOrder: nodes.length } // Il nuovo materiale va alla fine
                } as any;
            } else {
                // Assicurati che abbia metadata
                if (!newMaterial.metadata) {
                    newMaterial.metadata = {};
                }
                // Assegna displayOrder alla fine
                newMaterial.metadata.displayOrder = nodes.length;
            }

            return {
                id: newMaterial?.id || materialId,
                name: newMaterial?.name || name,
                type: 'material' as const,
                children: [],
                visible: true, // I nuovi materiali sono sempre visibili
                data: {
                    // Salva il materiale reale o mock
                    material: newMaterial,
                    // Mantieni anche le proprietà scomposte per compatibilità
                    variantId: 1,
                    materialType: 'pbr',
                    diffuseColor: '#FFFFFF',
                    specularColor: '#FFFFFF',
                    emissiveColor: '#000000',
                    roughness: 0.5,
                    metallic: 0.0,
                    enabled: true,
                    visible: true
                }
            };
        },
        [sceneEditor, nodes]
    );

    // Funzione per creare un nuovo gruppo
    const createNewGroup = useCallback((groupName: string): MaterialNode => {
        const groupId = `group-${Date.now()}`;

        return {
            id: groupId,
            name: groupName,
            type: 'group' as const,
            children: [],
            customIcon: <FolderIcon style={{ width: '16px', height: '16px', fill: '#666' }} />, // Icona di cartella/gruppo
            visible: true, // I gruppi sono sempre visibili
            data: {
                groupName: groupName,
                materialIds: []
            }
        };
    }, []);

    const handleNewMaterial = () => {
        treeState.closeOverlay();
        let formData = { name: '' };

        showDialog(
            'new-material-dialog',
            <Dialog
                title='New Material'
                buttons={[
                    {
                        label: 'OK',
                        onClick: () => {
                            const val = formData.name.trim();
                            if (!val) return;

                            const newMaterial = createNewMaterial(val);

                            // Aggiungi il materiale al tree state
                            treeState.addNode(newMaterial);

                            // USA IL PATTERN DI OBJECTEDITOR: prepareMaterials()
                            prepareMaterials();

                            closeDialog('new-material-dialog');
                        }
                    }
                ]}
            >
                <FormControl label='Material Name'>
                    <Input
                        placeholder='type material name...'
                        fullWidth
                        onChange={(e) => {
                            formData.name = e.target.value;
                        }}
                    />
                </FormControl>
            </Dialog>
        );
    };

    // Funzione per aggiornare i materiali dopo l'import (como prepareModel in objectEditor)
    const prepareMaterials = useCallback(() => {
        console.log('🔄 Preparing materials after import...');

        // Aggiorna i materiali nel MaterialsEditor dopo l'import (come objectEditor)
        if (sceneEditor) {
            console.log('🔄 Getting updated materials from SceneEditor');

            // USA IL PATTERN ESATTO DI OBJECTEDITOR: setTrigger + getAll + notify
            setMaterialRefreshTrigger(true);
            const updatedMaterials = sceneEditor.getAllMaterials();
            console.log('📊 Updated materials count:', updatedMaterials.length);

            // Notifica il componente padre con i materiali aggiornati
            if (onMaterialsUpdated) {
                onMaterialsUpdated(updatedMaterials);
            }
        }
    }, [sceneEditor, onMaterialsUpdated]);

    const handleImport = () => {
        // Verifica se la feature MaterialLibrary è disponibile
        if (!additionalFeatures?.materialLibrary) {
            showDialog(
                'Upgrade_Plan',
                <UpgradeBanner
                    marketPlace={sellerMarketplace ?? undefined}
                    message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Material Library')}
                    onUpgradeCompleted={async () => {
                        updateSubscription();
                    }}
                />
            );
            return;
        }

        treeState.closeOverlay();
        showDialog(
            'import-materials',
            <MaterialImportDialog
                sceneEditor={sceneEditor}
                onMaterialsUpdated={onMaterialsUpdated}
                onImport={async (selectedMaterials) => {
                    // Mostra notifica di import in corso
                    const importNotificationId = showInfoNotification({
                        message: T._('Importing materials...', 'Admin')
                    });

                    console.log('🔄 Import completed, preparing materials...', selectedMaterials.length);
                    console.log('selected materials:', selectedMaterials);
                    try {
                        // Attendiamo che SceneEditor completi l'import interno
                        await new Promise((resolve) => setTimeout(resolve, 100));
                        prepareMaterials();

                        // Rimuovi notifica di progresso e mostra successo
                        removeNotification(importNotificationId);
                        showSuccessNotification({
                            message: T._(`Successfully imported ${selectedMaterials.length} material(s)`, 'Admin')
                        });
                        console.log('✅ Import process completed successfully');
                    } catch (error) {
                        // Rimuovi notifica di progresso e mostra errore
                        removeNotification(importNotificationId);
                        showErrorNotification({
                            message: T._('Error importing materials: ' + (error as Error).message, 'Admin')
                        });
                        console.error('❌ Error preparing materials after import:', error);
                    }

                    // Chiudi il dialog dopo aver completato l'import
                    closeDialog('import-materials');
                }}
                onDelete={async (selectedMaterials) => {
                    // Mostra notifica di delete in corso
                    const deleteNotificationId = showInfoNotification({
                        message: T._('Deleting materials...', 'Admin')
                    });

                    console.log('🗑️ Delete completed, refreshing materials...', selectedMaterials.length);
                    try {
                        // La logica di delete è già gestita nel MaterialImportDialog
                        // Qui gestiamo solo le notifiche finali

                        // Rimuovi notifica di progresso e mostra successo
                        removeNotification(deleteNotificationId);
                        showSuccessNotification({
                            message: T._(`Successfully deleted ${selectedMaterials.length} material(s)`, 'Admin')
                        });
                        console.log('✅ Delete process completed successfully');
                    } catch (error) {
                        // Rimuovi notifica di progresso e mostra errore
                        removeNotification(deleteNotificationId);
                        showErrorNotification({
                            message: T._('Error deleting materials: ' + (error as Error).message, 'Admin')
                        });
                        console.error('❌ Error deleting materials:', error);
                    }
                }}
            />
        );
    };

    const tools = [
        {
            name: 'NEW',
            tooltip: 'Crea nuovo materiale',
            icon: <NewIcon />,
            onClick: handleNewMaterial
        },
        {
            name: 'GROUP',
            tooltip: 'Raggruppa materiali selezionati',
            icon: <GroupIcon />,
            disabled: selectedIds.length === 0, // Disabilitato se nessun elemento selezionato
            onClick: () => {
                if (!selectedIds.length) return;

                treeState.closeOverlay();
                let formData = { name: '' };

                showDialog(
                    'group-dialog',
                    <Dialog
                        title='Crea Gruppo Materiali'
                        buttons={[
                            {
                                label: 'OK',
                                onClick: () => {
                                    const groupName = formData.name.trim();
                                    if (!groupName) return;

                                    // Raccoglie gli ID dei materiali selezionati
                                    const selectedMaterialIds: string[] = [];
                                    const selectedNodes: MaterialNode[] = [];

                                    selectedIds.forEach((id) => {
                                        const node = findNodeById(id);
                                        if (node && node.type === 'material') {
                                            selectedMaterialIds.push(node.data.material?.id || node.id);
                                            selectedNodes.push(node);
                                        }
                                    });

                                    if (selectedMaterialIds.length === 0) {
                                        closeDialog('group-dialog');
                                        return;
                                    }

                                    // Usa SceneEditor per creare il gruppo logico
                                    if (sceneEditor && sceneEditor.setMaterialsGroup) {
                                        sceneEditor.setMaterialsGroup(selectedMaterialIds, groupName, true);
                                    }

                                    // Imposta il displayOrder del gruppo nella Map (come nel vecchio SceneEditorPage)
                                    if (selectedNodes.length > 0) {
                                        // Ordina i materiali selezionati per displayOrder
                                        const sortedMaterials = selectedNodes
                                            .filter((node) => node.data.material && node.data.material.metadata)
                                            .sort((a, b) => (a.data.material.metadata?.displayOrder || 0) - (b.data.material.metadata?.displayOrder || 0));

                                        // Usa il displayOrder del primo materiale per il gruppo
                                        const groupDisplayOrder = sortedMaterials.length > 0 ? sortedMaterials[0].data.material.metadata?.displayOrder || 0 : 0;

                                        console.log('🏷️ Setting group displayOrder:', groupName, groupDisplayOrder);
                                        setGroupMaterialsDisplayOrder((prev: Map<string, number>) => new Map(prev.set(groupName, groupDisplayOrder)));

                                        // Azzera tutti i displayOrder dei materiali nel gruppo e li riordina
                                        selectedNodes.forEach((node, index) => {
                                            if (node.data.material) {
                                                if (!node.data.material.metadata) {
                                                    node.data.material.metadata = {};
                                                }
                                                node.data.material.metadata.displayOrder = index;
                                            }
                                        });
                                    }

                                    // Crea il nodo gruppo nel tree
                                    const newGroup = createNewGroup(groupName);
                                    newGroup.data.materialIds = selectedMaterialIds;

                                    // Raggruppa i nodi visivamente
                                    groupSelectedNodes(
                                        groupName,
                                        () => newGroup,
                                        (nodeId: string) => treeState.findParentId(nodeId)
                                    );
                                    handleTreeDataChanged(nodes);

                                    // USA IL PATTERN DI OBJECTEDITOR: prepareMaterials()
                                    prepareMaterials();

                                    closeDialog('group-dialog');
                                }
                            }
                        ]}
                    >
                        <FormControl label='Nome del gruppo'>
                            <Input
                                placeholder='Nome del gruppo...'
                                fullWidth
                                onChange={(e) => {
                                    formData.name = e.target.value;
                                }}
                            />
                        </FormControl>
                    </Dialog>
                );
            }
        },
        {
            name: 'RENAME',
            tooltip: 'Rinomina elemento selezionato',
            icon: <RenameIcon />,
            disabled: selectedIds.length !== 1, // Disabilitato se non è selezionato esattamente 1 elemento
            onClick: () => {
                if (selectedIds.length !== 1) return; // Solo un elemento alla volta
                treeState.closeOverlay();

                const selectedNode = findNodeById(Array.from(selectedIds)[0]);
                if (!selectedNode) return;

                const currentName = selectedNode.type === 'material' ? selectedNode.data.material?.name || selectedNode.name : selectedNode.name;
                let formData = { name: currentName };

                showDialog(
                    'rename-dialog',
                    <Dialog
                        title={`Rinomina ${selectedNode.type === 'material' ? 'Materiale' : 'Gruppo'}`}
                        buttons={[
                            {
                                label: 'OK',
                                onClick: () => {
                                    const newName = formData.name.trim();
                                    if (!newName || newName === currentName) {
                                        closeDialog('rename-dialog');
                                        return;
                                    }

                                    if (selectedNode.type === 'material' && selectedNode.data.material) {
                                        // Rinomina materiale usando SceneEditor
                                        if (sceneEditor && sceneEditor.renameMaterial && selectedNode.data.material.id) {
                                            sceneEditor.renameMaterial(selectedNode.data.material.id, newName);
                                        }

                                        // Aggiorna il nodo nel tree
                                        selectedNode.name = newName;
                                        if (selectedNode.data.material) {
                                            selectedNode.data.material.name = newName;
                                        }
                                    } else if (selectedNode.type === 'group') {
                                        // Rinomina gruppo
                                        selectedNode.name = newName;

                                        // Se è un gruppo con materialIds, aggiorna anche il gruppo logico in SceneEditor
                                        if (selectedNode.data.materialIds && sceneEditor && sceneEditor.setMaterialsGroup) {
                                            sceneEditor.setMaterialsGroup(selectedNode.data.materialIds, newName, true);
                                        }
                                    }

                                    // Aggiorna il tree
                                    handleTreeDataChanged(nodes);

                                    // USA IL PATTERN DI OBJECTEDITOR: prepareMaterials()
                                    prepareMaterials();

                                    closeDialog('rename-dialog');
                                }
                            }
                        ]}
                    >
                        <FormControl label={`Nome del ${selectedNode.type === 'material' ? 'materiale' : 'gruppo'}`}>
                            <Input
                                placeholder={`Nome del ${selectedNode.type === 'material' ? 'materiale' : 'gruppo'}...`}
                                fullWidth
                                defaultValue={currentName}
                                onChange={(e) => {
                                    formData.name = e.target.value;
                                }}
                            />
                        </FormControl>
                    </Dialog>
                );
            }
        },
        {
            name: 'IMPORT',
            tooltip: 'Importa un nuovo materiale',
            icon: <ImportIcon />,
            onClick: handleImport
        },
        {
            name: 'CLONE',
            tooltip: 'Clona elementi selezionati',
            icon: <CloneIcon />,
            disabled: selectedIds.length === 0, // Disabilitato se nessun elemento selezionato
            onClick: () => {
                if (!selectedIds.length) return;
                treeState.closeOverlay();

                // Clona ogni materiale selezionato usando SceneEditor
                const newMaterialNodes: MaterialNode[] = [];

                selectedIds.forEach((selectedId) => {
                    const selectedNode = findNodeById(selectedId);
                    if (selectedNode) {
                        if (selectedNode.type === 'material' && selectedNode.data.material) {
                            const originalMaterial = selectedNode.data.material;
                            const clonedName = `${originalMaterial.name}-copy`;

                            // Usa SceneEditor per clonare il materiale
                            let clonedMaterial: BABYLON.Material | null = null;
                            if (sceneEditor && sceneEditor.cloneMaterial && originalMaterial.id) {
                                clonedMaterial = sceneEditor.cloneMaterial(originalMaterial.id, undefined, clonedName);
                            }

                            // Se la clonazione è riuscita, aggiungi al tree
                            if (clonedMaterial) {
                                // Assicurati che il materiale clonato abbia metadata
                                if (!clonedMaterial.metadata) {
                                    clonedMaterial.metadata = {};
                                }
                                // Il materiale clonato va alla fine
                                clonedMaterial.metadata.displayOrder = nodes.length + newMaterialNodes.length;

                                const clonedNode: MaterialNode = {
                                    id: clonedMaterial.id,
                                    name: clonedMaterial.name || clonedName,
                                    type: 'material' as const,
                                    children: [],
                                    data: {
                                        material: clonedMaterial,
                                        variantId: originalMaterial.variantId || 1,
                                        materialType: originalMaterial.materialType || 'pbr',
                                        diffuseColor: originalMaterial.diffuseColor || '#FFFFFF',
                                        specularColor: originalMaterial.specularColor || '#FFFFFF',
                                        emissiveColor: originalMaterial.emissiveColor || '#000000',
                                        roughness: originalMaterial.roughness ?? 0.5,
                                        metallic: originalMaterial.metallic ?? 0.0,
                                        enabled: originalMaterial.enabled ?? true,
                                        visible: originalMaterial.visible ?? true
                                    }
                                };

                                treeState.addNode(clonedNode);
                                newMaterialNodes.push(clonedNode);
                            }
                        } else if (selectedNode.type === 'group') {
                            // Per i gruppi, crea una copia del gruppo
                            const clonedGroup = createNewGroup(`${selectedNode.name}-copy`);

                            // Clona tutti i materiali figli del gruppo
                            if (selectedNode.children) {
                                (selectedNode.children as MaterialNode[]).forEach((child: MaterialNode) => {
                                    if (child.type === 'material' && child.data.material) {
                                        const originalMaterial = child.data.material;
                                        const clonedName = `${originalMaterial.name}-copy`;

                                        let clonedMaterial: BABYLON.Material | null = null;
                                        if (sceneEditor && sceneEditor.cloneMaterial && originalMaterial.id) {
                                            clonedMaterial = sceneEditor.cloneMaterial(originalMaterial.id, undefined, clonedName);
                                        }

                                        if (clonedMaterial) {
                                            // Assicurati che il materiale clonato abbia metadata
                                            if (!clonedMaterial.metadata) {
                                                clonedMaterial.metadata = {};
                                            }
                                            // Per i materiali clonati nei gruppi, mantieni l'ordine originale
                                            clonedMaterial.metadata.displayOrder = child.data.material.metadata?.displayOrder ?? 0;

                                            const clonedChildNode: MaterialNode = {
                                                id: clonedMaterial.id,
                                                name: clonedMaterial.name || clonedName,
                                                type: 'material' as const,
                                                children: [],
                                                data: {
                                                    material: clonedMaterial,
                                                    variantId: originalMaterial.variantId || 1,
                                                    materialType: originalMaterial.materialType || 'pbr',
                                                    diffuseColor: originalMaterial.diffuseColor || '#FFFFFF',
                                                    specularColor: originalMaterial.specularColor || '#FFFFFF',
                                                    emissiveColor: originalMaterial.emissiveColor || '#000000',
                                                    roughness: originalMaterial.roughness ?? 0.5,
                                                    metallic: originalMaterial.metallic ?? 0.0,
                                                    enabled: originalMaterial.enabled ?? true,
                                                    visible: originalMaterial.visible ?? true
                                                }
                                            };
                                            clonedGroup.children!.push(clonedChildNode);
                                        }
                                    }
                                });
                            }

                            treeState.addNode(clonedGroup);
                        }
                    }
                });

                // USA IL PATTERN DI OBJECTEDITOR: prepareMaterials()
                prepareMaterials();
            }
        },
        {
            name: 'SAVE',
            tooltip: 'Salva materiali selezionati nella libreria',
            icon: <SaveIcon />,
            disabled: selectedIds.length === 0, // Disabilitato se nessun elemento selezionato
            onClick: async () => {
                // Check della feature additionalFeatures.materialLibrary
                if (!additionalFeatures?.materialLibrary) {
                    showDialog(
                        'Upgrade_Plan',
                        <UpgradeBanner
                            marketPlace={sellerMarketplace ?? undefined}
                            message={stringFormat(
                                T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'),
                                'Material Library'
                            )}
                            onUpgradeCompleted={async () => {
                                updateSubscription();
                            }}
                        />
                    );
                    return;
                }

                // Chiude eventuali overlay aperti
                treeState.closeOverlay();

                // Imposta il token globale dal cookie zakeke_token
                const getCookieValue = (name: string): string | null => {
                    const value = `; ${document.cookie}`;
                    const parts = value.split(`; ${name}=`);
                    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
                    return null;
                };

                const zakekeToken = getCookieValue('zakeke_token');
                if (zakekeToken) {
                    // Imposta il token globalmente per le chiamate API della 3D core
                    (window as any).zakekeToken = zakekeToken;

                    // Imposta il token per jQuery AJAX globalmente
                    if ((window as any).$ && (window as any).$.ajaxSetup) {
                        (window as any).$.ajaxSetup({
                            beforeSend: function (xhr: any) {
                                xhr.setRequestHeader('Authorization', `Bearer ${zakekeToken}`);
                            }
                        });
                    }

                    // Imposta il token per fetch globalmente
                    const originalFetch = window.fetch;
                    window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
                        const modifiedInit: RequestInit = {
                            ...init,
                            headers: {
                                ...init?.headers,
                                Authorization: `Bearer ${zakekeToken}`
                            }
                        };
                        return originalFetch.call(this, input, modifiedInit);
                    };

                    // Imposta anche come header authorization se necessario per Zakeke
                    if ((window as any).Zakeke && (window as any).Zakeke.setAuthToken) {
                        (window as any).Zakeke.setAuthToken(zakekeToken);
                    }

                    console.log('🔑 Set global zakekeToken for jQuery, fetch and Zakeke:', zakekeToken.substring(0, 10) + '...');
                } else {
                    console.warn('⚠️ No zakeke_token found in cookies');
                }

                // Funzione saveMaterials che usa makeLibraryMaterial della 3D core
                const saveMaterials = async (materialName?: string) => {
                    const selectedMaterials = selectedIds
                        .map((selectedId) => findNodeById(selectedId))
                        .filter((node) => node && node.type === 'material' && node.data.material)
                        .map((node) => node!.data.material);

                    if (selectedMaterials.length === 1 && !materialName?.trim()) {
                        showErrorNotification({
                            message: T._('Please enter a valid material name', 'Admin')
                        });
                        return;
                    }

                    // Mostra notifica di inizio salvataggio
                    const saveNotificationId = showInfoNotification({
                        message: T._('Saving materials to library...', 'Admin')
                    });

                    console.log(
                        '💾 Starting save to library process for materials:',
                        selectedMaterials.map((m) => m.name)
                    );

                    try {
                        let materialCount = 0;
                        for (const material of selectedMaterials) {
                            materialCount++;

                            console.log(`💾 Saving material [${materialCount}/${selectedMaterials.length}]: ${material.name}`);

                            // Usa sceneEditor.makeLibraryMaterial come nel vecchio SceneEditorPage
                            if (sceneEditor && sceneEditor.makeLibraryMaterial) {
                                const materialName_final = materialName || material.name || `Material ${materialCount}`;

                                try {
                                    await sceneEditor.makeLibraryMaterial(material.id, materialName_final);
                                    console.log(`✅ Successfully saved material via sceneEditor.makeLibraryMaterial: ${materialName_final}`);
                                } catch (materialError) {
                                    console.error(`❌ Error saving material ${materialName_final} via sceneEditor.makeLibraryMaterial:`, materialError);
                                    throw materialError;
                                }
                            } else {
                                const error = 'sceneEditor.makeLibraryMaterial method not available';
                                console.error(`❌ ${error}`);
                                throw new Error(error);
                            }
                        }

                        // Rimuovi notifica di progresso e mostra successo
                        removeNotification(saveNotificationId);
                        showSuccessNotification({
                            message: T._(`Successfully saved ${selectedMaterials.length} material(s) to library`, 'Admin')
                        });
                        console.log('✅ Save to library process completed');
                    } catch (error) {
                        // Rimuovi notifica di progresso e mostra errore
                        removeNotification(saveNotificationId);
                        showErrorNotification({
                            message: T._('Error saving materials to library: ' + (error as Error).message, 'Admin')
                        });
                        console.error('❌ Error in save process:', error);
                    }
                };

                // Logic di controllo materiali selezionati (come nel vecchio codice)
                const selectedMaterials = selectedIds.map((selectedId) => findNodeById(selectedId)).filter((node) => node && node.type === 'material');

                if (selectedMaterials.length === 0) {
                    alert('Select at least one material to save');
                } else if (selectedMaterials.some((node) => node!.type === 'group')) {
                    alert('Select only materials, not groups');
                } else {
                    // Se è selezionato un solo materiale, chiedi il nome tramite dialog
                    if (selectedMaterials.length === 1) {
                        const material = selectedMaterials[0]!.data.material;
                        let formData = { name: material.name || '' };

                        showDialog(
                            'save-material-dialog',
                            <Dialog
                                title='Salva materiale nella libreria'
                                buttons={[
                                    {
                                        label: 'Salva',
                                        onClick: async () => {
                                            const materialName = formData.name.trim();
                                            closeDialog('save-material-dialog');
                                            if (materialName) {
                                                await saveMaterials(materialName);
                                            }
                                        }
                                    }
                                ]}
                            >
                                <FormControl label='Nome del materiale nella libreria'>
                                    <Input
                                        placeholder='Nome del materiale nella libreria...'
                                        fullWidth
                                        defaultValue={material.name || ''}
                                        onChange={(e) => {
                                            formData.name = e.target.value;
                                        }}
                                    />
                                </FormControl>
                            </Dialog>
                        );
                    } else {
                        // Se sono selezionati più materiali, usa i loro nomi esistenti
                        await saveMaterials();
                    }
                }
            }
        },
        {
            name: 'REMOVE',
            tooltip: 'Rimuovi elemento selezionato',
            icon: <RemoveIcon />,
            disabled: selectedIds.length === 0, // Disabilitato se nessun elemento selezionato
            onClick: () => {
                if (!selectedIds.length) return;
                treeState.closeOverlay();

                showDialog(
                    'remove-dialog',
                    <Dialog
                        title='Remove Material'
                        showCloseButton={false}
                        buttons={[
                            {
                                label: 'Cancel',
                                onClick: () => closeDialog('remove-dialog')
                            },
                            {
                                label: 'Remove',
                                onClick: () => {
                                    // Rimuovi ogni materiale selezionato usando SceneEditor
                                    selectedIds.forEach((selectedId) => {
                                        const selectedNode = findNodeById(selectedId);
                                        if (selectedNode) {
                                            if (selectedNode.type === 'material' && selectedNode.data.material) {
                                                const material = selectedNode.data.material;

                                                // Usa SceneEditor per rimuovere il materiale
                                                if (sceneEditor && sceneEditor.deleteMaterial && material.id) {
                                                    sceneEditor.deleteMaterial(material.id);
                                                }

                                                // Rimuovi anche dal material group se esiste
                                                if (sceneEditor && sceneEditor.removeMaterialGroup && material.id) {
                                                    sceneEditor.removeMaterialGroup(material.id);
                                                }
                                            } else if (selectedNode.type === 'group') {
                                                // Per i gruppi, rimuovi prima tutti i materiali figli
                                                if (selectedNode.children) {
                                                    (selectedNode.children as MaterialNode[]).forEach((child: MaterialNode) => {
                                                        if (child.type === 'material' && child.data.material) {
                                                            const material = child.data.material;
                                                            if (sceneEditor && sceneEditor.deleteMaterial && material.id) {
                                                                sceneEditor.deleteMaterial(material.id);
                                                            }
                                                            if (sceneEditor && sceneEditor.removeMaterialGroup && material.id) {
                                                                sceneEditor.removeMaterialGroup(material.id);
                                                            }
                                                        }
                                                    });
                                                }
                                            }
                                        }
                                    });

                                    // Rimuovi dal tree state
                                    removeNodes(selectedIds);

                                    // USA IL PATTERN DI OBJECTEDITOR: prepareMaterials()
                                    prepareMaterials();

                                    closeDialog('remove-dialog');
                                }
                            }
                        ]}
                    >
                        <p>Are you sure you want to remove the selected material(s)? This action cannot be undone.</p>
                    </Dialog>
                );
            }
        }
    ];

    return (
        <div>
            <ToolGrid tools={tools} columns={tools.length} />
            <TreeComponent
                ref={treeRef}
                treeState={treeStateForComponent}
                filter={filter}
                setFilter={setFilter}
                showVisibilityIcon={false}
                onNodeMoved={(nodeId, targetParentId, index) => {
                    // Usa la funzione unificata che può cercare sia nei filteredData che nel treeState originale
                    const nodesToMove = treeState.selectedIds.has(nodeId) ? Array.from(treeState.selectedIds) : [nodeId];

                    // Trova il nodo target per verificare se è un gruppo
                    const targetNode = targetParentId ? findNodeByIdUnified(targetParentId) : null;

                    // Trova il nodo che viene trascinato
                    const draggedNode = findNodeByIdUnified(nodeId);

                    // INTERCETTA IMMEDIATAMENTE il drag di materiale su materiale per evitare il warning
                    if (draggedNode?.type === 'material' && targetNode?.type === 'material') {
                        // DRAG & DROP di materiali su un altro MATERIALE per RIORDINO

                        // DETERMINA SE SIAMO DENTRO UN GRUPPO O NELLA RADICE
                        const draggedParentId = treeState.findParentId(nodeId);
                        const targetParentId_actual = treeState.findParentId(targetParentId!);

                        // VERIFICA IL CONTESTO E GESTISCI I DRAG TRA CONTESTI DIVERSI
                        if (draggedParentId !== targetParentId_actual) {
                            // DRAG DA CONTESTI DIVERSI (es. da gruppo a root, o da root a gruppo)

                            // GESTISCI IL CASO: materiali da gruppo trascinati su materiale root
                            if (draggedParentId && !targetParentId_actual) {
                                // I materiali spostati devono:
                                // 1. Essere disassociati dal gruppo di origine
                                // 2. Essere assegnati al gruppo "_dummy_" (materiali root)
                                // 3. Avere displayOrder riorganizzato rispetto al target root

                                const targetMaterial = findNodeByIdUnified(targetParentId!);
                                if (!targetMaterial || targetMaterial.type !== 'material') {
                                    console.warn('❌ Target material not found');
                                    return;
                                }

                                const targetDisplayOrder = targetMaterial.data.material?.metadata?.displayOrder ?? 0;

                                // Sposta i materiali trascinati e riorganizza i displayOrder
                                nodesToMove.forEach((draggedNodeId, index) => {
                                    const draggedNode = findNodeByIdUnified(draggedNodeId);
                                    if (draggedNode && draggedNode.type === 'material') {
                                        const materialId = draggedNode.data.material?._internalId || draggedNode.data.material?.id;

                                        if (materialId && draggedNode.data.material) {
                                            // 1. TRACCIA COME MANUALMENTE DISASSOCIATO
                                            setManuallyUngroupedMaterials((prev) => new Set(prev.add(materialId)));

                                            // 2. DISASSOCIA DAL GRUPPO DI ORIGINE
                                            if (sceneEditor && sceneEditor.setMaterialsGroup) {
                                                sceneEditor.setMaterialsGroup([materialId], '_dummy_', true);
                                            }

                                            // 3. RIORGANIZZA DISPLAYORDER - inserisci dopo il target
                                            const newDisplayOrder = targetDisplayOrder + 1 + index;
                                            if (!draggedNode.data.material.metadata) {
                                                draggedNode.data.material.metadata = {};
                                            }
                                            draggedNode.data.material.metadata.displayOrder = newDisplayOrder;

                                            // 4. RIMUOVI DAL GRUPPO VISUALE
                                            const currentParentId = treeState.findParentId(draggedNodeId);
                                            if (currentParentId) {
                                                const parentGroup = findNodeById(currentParentId);
                                                if (parentGroup && parentGroup.type === 'group' && parentGroup.data.materialIds) {
                                                    parentGroup.data.materialIds = parentGroup.data.materialIds.filter((id) => id !== materialId);
                                                }
                                            }
                                        }
                                    }
                                });

                                // RIORDINA TUTTI I MATERIALI ROOT per mantenere la sequenza corretta
                                const allRootMaterials = nodes
                                    .filter((node) => node.type === 'material')
                                    .sort((a, b) => {
                                        const aOrder = a.data.material?.metadata?.displayOrder ?? 0;
                                        const bOrder = b.data.material?.metadata?.displayOrder ?? 0;
                                        return aOrder - bOrder;
                                    });

                                // SPOSTA visivamente nel tree
                                treeState.moveSelection(nodesToMove, null); // null = root level

                                // NOTIFICA IL CAMBIAMENTO
                                setTimeout(() => {
                                    if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                        onMaterialsUpdated(sceneEditor.getMaterials());
                                    }
                                }, 50);

                                return; // ESCI - drag gestito
                            }

                            // GESTISCI IL CASO: materiali root trascinati su materiale in gruppo
                            if (!draggedParentId && targetParentId_actual) {
                                const targetGroup = findNodeByIdUnified(targetParentId_actual);
                                const targetMaterial = findNodeByIdUnified(targetParentId!);

                                if (!targetGroup || targetGroup.type !== 'group' || !targetMaterial) {
                                    console.warn('❌ Target group or material not found');
                                    return;
                                }

                                // I materiali spostati devono:
                                // 1. Essere rimossi dal tracking manualmente disassociati
                                // 2. Essere assegnati al gruppo target
                                // 3. Avere internalDisplayOrder riorganizzato rispetto al target nel gruppo

                                const targetInternalOrder = targetMaterial.data.material?.metadata?.internalDisplayOrder ?? 0;

                                const materialIdsToGroup: string[] = [];

                                nodesToMove.forEach((draggedNodeId, index) => {
                                    const draggedNode = findNodeByIdUnified(draggedNodeId);
                                    if (draggedNode && draggedNode.type === 'material') {
                                        const materialId = draggedNode.data.material?._internalId || draggedNode.data.material?.id;

                                        if (materialId && draggedNode.data.material) {
                                            // 1. RIMUOVI DAL TRACKING DISASSOCIATI
                                            if (manuallyUngroupedMaterials.has(materialId)) {
                                                setManuallyUngroupedMaterials((prev) => {
                                                    const newSet = new Set(prev);
                                                    newSet.delete(materialId);
                                                    return newSet;
                                                });
                                            }

                                            // 2. AGGIUNGI AL GRUPPO TARGET
                                            materialIdsToGroup.push(materialId);

                                            // 3. ASSEGNA internalDisplayOrder - inserisci dopo il target
                                            const newInternalOrder = targetInternalOrder + 1 + index;
                                            if (!draggedNode.data.material.metadata) {
                                                draggedNode.data.material.metadata = {};
                                            }
                                            draggedNode.data.material.metadata.internalDisplayOrder = newInternalOrder;
                                        }
                                    }
                                });

                                // AGGIORNA IL GRUPPO nel SceneEditor
                                if (materialIdsToGroup.length > 0 && sceneEditor && sceneEditor.setMaterialsGroup) {
                                    const existingMaterialIds = targetGroup.data.materialIds || [];
                                    const allMaterialIds = [...new Set([...existingMaterialIds, ...materialIdsToGroup])];

                                    sceneEditor.setMaterialsGroup(allMaterialIds, targetGroup.name, true);

                                    // Aggiorna i materialIds del gruppo
                                    targetGroup.data.materialIds = allMaterialIds;
                                }

                                // SPOSTA visivamente nel tree
                                treeState.moveSelection(nodesToMove, targetParentId_actual);

                                // FORZA IL RE-RENDER
                                // NOTIFICA IL CAMBIAMENTO
                                setTimeout(() => {
                                    if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                        onMaterialsUpdated(sceneEditor.getMaterials());
                                    }
                                }, 50);

                                return; // ESCI - drag gestito
                            }

                            // Altri tipi di drag cross-context possono essere gestiti qui
                            console.warn('❌ Cross-context drag not supported for this combination');
                            return;
                        }

                        if (draggedParentId && targetParentId_actual) {
                            // **RIORDINO INTERNO AL GRUPPO** - APPROCCIO SEMPLIFICATO

                            const parentGroup = findNodeByIdUnified(draggedParentId);
                            if (!parentGroup || parentGroup.type !== 'group') {
                                console.warn('❌ Parent group not found');
                                return;
                            }

                            // Ottieni tutti i materiali del gruppo
                            const groupMaterials = ((parentGroup.children as MaterialNode[]) || []).filter((child) => child.type === 'material');

                            const materialsBeingMoved = groupMaterials.filter((m) => nodesToMove.includes(m.id));
                            const targetMaterial = groupMaterials.find((m) => m.id === targetParentId);

                            if (materialsBeingMoved.length === 0 || !targetMaterial) {
                                console.warn('❌ Could not find moved or target materials in group');
                                return;
                            }

                            // Rimuovi i materiali trascinati dalla lista
                            const materialsWithoutMoved = groupMaterials.filter((m) => !nodesToMove.includes(m.id));

                            // Trova dove inserire (dopo il target)
                            const targetIndex = materialsWithoutMoved.findIndex((m) => m.id === targetParentId);
                            const insertIndex = targetIndex + 1;

                            // Crea il nuovo ordine
                            const reorderedMaterials = [
                                ...materialsWithoutMoved.slice(0, insertIndex),
                                ...materialsBeingMoved,
                                ...materialsWithoutMoved.slice(insertIndex)
                            ];

                            // Aggiorna SOLO i displayOrder interni
                            reorderedMaterials.forEach((material, index) => {
                                if (material.data.material) {
                                    if (!material.data.material.metadata) material.data.material.metadata = {};
                                    material.data.material.metadata.internalDisplayOrder = index;
                                }
                            });

                            // Aggiorna SOLO i children del gruppo
                            parentGroup.children = reorderedMaterials;

                            // Forza re-render con updateNode invece di setNodes
                            updateNode(parentGroup.id, { children: reorderedMaterials });

                            // Notifica SceneEditor
                            setTimeout(() => {
                                if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                    onMaterialsUpdated(sceneEditor.getMaterials());
                                }
                            }, 0);

                            return; // Esci - tutto gestito
                        } else {
                            // **RIORDINO NELLA RADICE (livello globale)**

                            // OTTIMIZZAZIONE: Riordina TUTTI i materiali selezionati basandosi sulla nuova posizione
                            const allMaterialNodes = nodes.filter((node) => node.type === 'material');
                            const materialsBeingMoved = allMaterialNodes.filter((n) => nodesToMove.includes(n.id));
                            const targetMaterialNode = allMaterialNodes.find((n) => n.id === targetParentId);

                            if (materialsBeingMoved.length === 0 || !targetMaterialNode) {
                                console.warn('❌ Could not find moved or target material nodes');
                                return;
                            }

                            // Rimuovi TUTTI i materiali selezionati dalla loro posizione attuale
                            const materialsWithoutMoved = allMaterialNodes.filter((n) => !nodesToMove.includes(n.id));

                            // Trova l'indice dove inserire i materiali (dopo il target)
                            const targetIndex = materialsWithoutMoved.findIndex((n) => n.id === targetParentId);
                            const insertIndex = targetIndex + 1;

                            // Inserisci TUTTI i materiali selezionati nella nuova posizione
                            const reorderedMaterials = [
                                ...materialsWithoutMoved.slice(0, insertIndex),
                                ...materialsBeingMoved,
                                ...materialsWithoutMoved.slice(insertIndex)
                            ];

                            // RIASSEGNA DISPLAYORDER SEQUENZIALI PER EVITARE DUPLICATI
                            reorderedMaterials.forEach((material, index) => {
                                if (material.data.material) {
                                    // Assicurati che abbia metadata
                                    if (!material.data.material.metadata) {
                                        material.data.material.metadata = {};
                                    }
                                    // Assegna displayOrder sequenziale basato sulla nuova posizione
                                    material.data.material.metadata.displayOrder = index;
                                }
                            });

                            // CREA UNA COPIA COMPLETA DEI NODI con i nuovi displayOrder
                            const nodesCopy = nodes.map((node) => {
                                if (node.type === 'material') {
                                    // Trova il materiale riordinato corrispondente
                                    const reorderedMaterial = reorderedMaterials.find((m) => m.id === node.id);
                                    if (reorderedMaterial && reorderedMaterial.data.material && node.data.material) {
                                        return {
                                            ...node,
                                            data: {
                                                ...node.data,
                                                material: {
                                                    ...node.data.material,
                                                    metadata: {
                                                        ...node.data.material.metadata,
                                                        displayOrder: reorderedMaterial.data.material.metadata?.displayOrder
                                                    }
                                                }
                                            }
                                        };
                                    }
                                }
                                return node; // Nodi gruppo rimangono invariati
                            });

                            // RIORDINA I NODI FINALI in base al nuovo displayOrder
                            const finalNodes = nodesCopy.sort((a, b) => {
                                if (a.type === 'material' && b.type === 'material' && a.data.material && b.data.material) {
                                    const aOrder = a.data.material.metadata?.displayOrder ?? 0;
                                    const bOrder = b.data.material.metadata?.displayOrder ?? 0;
                                    return aOrder - bOrder;
                                }
                                // I gruppi mantengono la loro posizione relativa
                                return 0;
                            });

                            // FORZA IL RE-RENDER COMPLETO sostituendo completamente i nodi
                            setNodes(finalNodes);
                        }

                        // NOTIFICA LO SCENE EDITOR con i materiali aggiornati
                        if (sceneEditor && sceneEditor.getMaterials) {
                            const updatedMaterials = sceneEditor.getMaterials();
                            if (onMaterialsUpdated) {
                                onMaterialsUpdated(updatedMaterials);
                            }
                        }

                        // ASSICURATI che i materiali riordinati a root rimangano nel gruppo _dummy_
                        setTimeout(() => {
                            const currentRootMaterials = nodes
                                .filter((node: MaterialNode) => node.type === 'material')
                                .map((node: MaterialNode) => node.data.material?._internalId || node.data.material?.id)
                                .filter((id: string | undefined) => id) as string[];

                            if (currentRootMaterials.length > 0 && sceneEditor && sceneEditor.setMaterialsGroup) {
                                sceneEditor.setMaterialsGroup(currentRootMaterials, '_dummy_', true);
                            }
                        }, 50);

                        // ESCI IMMEDIATAMENTE - non processare altro
                        return;
                    }

                    if (targetNode && targetNode.type === 'group') {
                        // DRAG & DROP di materiali su un GRUPPO
                        // Filtra solo i materiali (non altri gruppi)
                        const materialIds: string[] = [];
                        const materialNodesToMove: MaterialNode[] = [];

                        nodesToMove.forEach((nodeId) => {
                            const node = findNodeById(nodeId);
                            if (node && node.type === 'material' && node.data.material) {
                                // Usa la stessa logica di generazione ID dei nodi TreeComponent
                                const materialId = node.data.material._internalId || node.data.material.id;
                                if (materialId) {
                                    materialIds.push(materialId);
                                    materialNodesToMove.push(node);
                                }
                            }
                        });

                        if (materialIds.length > 0) {
                            // RIMUOVI i materiali dalla lista di quelli manualmente disassociati
                            // Quando un materiale viene spostato manualmente in un gruppo, non è più "manually ungrouped"
                            const materialNodesToUpdate = materialNodesToMove.filter((node) => {
                                const materialId = node.data.material?._internalId || node.data.material?.id;
                                if (materialId && manuallyUngroupedMaterials.has(materialId)) {
                                    setManuallyUngroupedMaterials((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.delete(materialId);
                                        return newSet;
                                    });
                                    return true;
                                }
                                return false;
                            });

                            // Aggiorna il gruppo logico in SceneEditor
                            if (sceneEditor && sceneEditor.setMaterialsGroup) {
                                // Ottieni i materiali già presenti nel gruppo
                                const existingMaterialIds = targetNode.data.materialIds || [];
                                // Combina con i nuovi materiali (evita duplicati)
                                const allMaterialIds = [...new Set([...existingMaterialIds, ...materialIds])];

                                sceneEditor.setMaterialsGroup(allMaterialIds, targetNode.name, true);

                                // Aggiorna i materialIds del gruppo
                                targetNode.data.materialIds = allMaterialIds;
                            }

                            // Sposta visualmente i nodi nel tree
                            treeState.moveSelection(nodesToMove, targetParentId);
                        } else {
                            console.warn('⚠️ No valid materials to add to group');
                        }
                    } else {
                        // **GESTISCI LA DISASSOCIAZIONE DAI GRUPPI**
                        // Se i materiali vengono spostati fuori da un gruppo (targetParentId è null = root)
                        if (targetParentId === null) {
                            // PRIMA disassocia dal SceneEditor, POI sposta nel tree
                            nodesToMove.forEach((nodeId) => {
                                const node = findNodeById(nodeId);
                                const currentParentId = treeState.findParentId(nodeId);

                                if (node && node.type === 'material' && currentParentId) {
                                    // Il materiale sta uscendo da un gruppo
                                    const parentGroup = findNodeById(currentParentId);
                                    if (parentGroup && parentGroup.type === 'group') {
                                        // TRACCIA IL MATERIALE COME MANUALMENTE DISASSOCIATO
                                        // Usa la stessa logica di generazione ID dei nodi TreeComponent
                                        const materialId = node.data.material?._internalId || node.data.material?.id;
                                        if (materialId) {
                                            setManuallyUngroupedMaterials((prev) => new Set(prev.add(materialId)));
                                            console.log('📝 Added to manually ungrouped:', materialId);
                                        }

                                        // DISASSOCIA IL MATERIALE DAL GRUPPO LOGICO nel SceneEditor
                                        // STRATEGIA: invece di solo rimuovere, spostiamo nel gruppo "_dummy_"
                                        if (sceneEditor && materialId) {
                                            if (sceneEditor.setMaterialsGroup) {
                                                // Sposta il materiale nel gruppo "_dummy_" che rappresenta materiali non raggruppati
                                                sceneEditor.setMaterialsGroup([materialId], '_dummy_', true);
                                            } else if (sceneEditor.removeMaterialGroup) {
                                                sceneEditor.removeMaterialGroup(materialId);
                                            }
                                        }

                                        // RIMUOVI IL MATERIALE dalla lista materialIds del gruppo
                                        if (parentGroup.data.materialIds && materialId) {
                                            parentGroup.data.materialIds = parentGroup.data.materialIds.filter((id) => id !== materialId);
                                        }
                                    }
                                }
                            });

                            // SPOSTA visualmente nel tree DOPO la disassociazione
                            treeState.moveSelection(nodesToMove, targetParentId);

                            // FORZA UN AGGIORNAMENTO RITARDATO per permettere al SceneEditor di processare il cambio
                            setTimeout(() => {
                                console.log('🔄 Forcing delayed update after disassociation');

                                // Se disponibile, ricarica i materiali dal SceneEditor per sincronizzare
                                if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                                    console.log('🔄 Reloading materials from SceneEditor after disassociation');
                                    onMaterialsUpdated(sceneEditor.getMaterials());
                                }
                            }, 100);
                            return; // Esci senza propagare ulteriori aggiornamenti
                        }

                        // I nodi mantengono la loro struttura originale e NON vengono raggruppati
                        treeState.moveSelection(nodesToMove, targetParentId);
                    }

                    // Propaga gli aggiornamenti al SceneEditor (solo per operazioni normali)
                    handleTreeDataChanged(nodes);

                    // Notifica il cambiamento (solo per operazioni normali)
                    if (onMaterialsUpdated && sceneEditor && sceneEditor.getMaterials) {
                        onMaterialsUpdated(sceneEditor.getMaterials());
                    }
                }}
            />
        </div>
    );
};

export default MaterialsEditor;
